import React, { useEffect, useRef, useState } from "react";
import { Checkbox, DatePicker, Form, Image, Input } from "antd";
import TextArea from "antd/es/input/TextArea";
import { FiSave, FiTrash2, FiPrinter, FiChevronDown, FiChevronUp, FiFileText } from "react-icons/fi";
import { message, Popconfirm } from "antd";
import dayjs from "dayjs";
import { calculateAge } from "../../../../utils/helpers";
import drEasyApi from "../../../../Api/Customapi";
import { buildPrintLymphedemaHTML } from "./PrintAssemble";

const ROW_COUNT = 9;
const ROW_INDICES = Array.from({ length: ROW_COUNT }, (_, i) => i);

// ── API helpers ───────────────────────────────────────────────────────────────

function buildMeasurementPayload(pointOfMeasurement, idx, f) {
    const payload = { pointOfMeasurement };
    [1, 2, 3, 4].forEach((col) => {
        const raw = f[`session${col}Date`];
        payload[`session${col}Date`] =
            raw && dayjs.isDayjs(raw) && raw.isValid() ? raw.toISOString() : null;
        payload[`session${col}RT`] = Number(f[`ptMeas_${idx}_${col}_RT`]) || 0;
        payload[`session${col}LT`] = Number(f[`ptMeas_${idx}_${col}_LT`]) || 0;
    });
    return payload;
}

async function addMeasurement(reportId, pointOfMeasurement, idx, f) {
    const res = await drEasyApi.post("/api/Reports/AddLymphedemaMeasurement", {
        reportId,
        ...buildMeasurementPayload(pointOfMeasurement, idx, f),
    });
    return res.data;
}

async function updateMeasurement(measurementId, pointOfMeasurement, idx, f) {
    const res = await drEasyApi.put("/api/Reports/UpdateLymphedemaMeasurement", {
        measurementId,
        ...buildMeasurementPayload(pointOfMeasurement, idx, f),
    });
    return res.data;
}

async function deleteMeasurementApi(measurementId) {
    await drEasyApi.delete("/api/Reports/DeleteLymphedemaMeasurement", {
        params: { MeasurementId: measurementId },
    });
}

// ── Shared form body ──────────────────────────────────────────────────────────

function LymphedemaFormContent({
    assessmentForm,
    patientData,
    lymphedemaData,
    physioTherapyData,
    savingAssessment,
    setSavingAssessment,
    setAssessmentApiState,
    onSave,
    fetchUserReport,
    reportId,
    measurementIds,
    setMeasurementIds,
}) {
    const [deletingRow, setDeletingRow] = useState(null);
    const [savingRow, setSavingRow]     = useState(null);

    // ── Save all ───────────────────────────────────────────────────────────────
    const handleSaveAssessment = async () => {
        setSavingAssessment(true);
        try {
            const f = assessmentForm.getFieldsValue();
            const treatmentOpts = f.ptTreatmentOptions || [];
            const updatedIds = { ...measurementIds };

            await Promise.all(
                ROW_INDICES.map(async (idx) => {
                    const pointOfMeasurement = (f[`ptMeasPoint_${idx}`] ?? "").trim();
                    if (!pointOfMeasurement) return;
                    const existingId = updatedIds[idx];
                    if (existingId) {
                        await updateMeasurement(existingId, pointOfMeasurement, idx, f);
                    } else {
                        const result = await addMeasurement(reportId, pointOfMeasurement, idx, f);
                        if (result?.id) updatedIds[idx] = result.id;
                    }
                })
            );

            setMeasurementIds(updatedIds);

            const assessmentFields = {
                diagnosis:           f.ptDiagnosis                       || "",
                pastHistory:         f.ptPastHistory                     || "",
                chiefComplain:       f.ptChiefComplain                   || "",
                jobAndOccupation:    f.ptJob                             || "",
                painScore:           Number(f.ptPainScore)               || 0,
                painDistribution:    f.ptPainDistribution                || "",
                lymphaticEdema:      f.ptLymphaticEdema?.includes("yes") ?? false,
                radioInvestigations: f.ptRadioInvestigations             || "",
                labNotes:            f.ptLabNotes?.includes("yes")       ?? false,
                labResults:          f.ptLabResults                      || "",
                precautions:         f.ptPrecautions                     || "",
                ptAssessment:        f.ptAssessment                      || "",
                ptPlanOfTreatment:   f.ptPlanOfTreatment                 || "",
                sessionsNum:         Number(f.sessionsNum?.[0])          || 0,
                progressNotes:       f.ptProgressNotes                   || "",
                tttGoals:            f.ptTttGoals                        || "",
                discharge:           f.ptDischarge                       || "",
                otherNotes:          f.ptOtherNotes                      || "",
                operations:          f.ptOperations                      || "",
                presentHistory:      f.ptPresentHistory                  || "",
                skinAbnormalities:   f.ptSkinAbnormalities               || "",
                treatmentMLD:        treatmentOpts.includes("MLD"),
                treatmentPCD:        treatmentOpts.includes("PCD"),
                treatmentTECAR:      treatmentOpts.includes("TECAR"),
                treatmentBandaging:  treatmentOpts.includes("Bandaging"),
                treatmentExercises:  treatmentOpts.includes("Exercises"),
                treatmentOther:      f.ptTreatmentOther                  || "",
            };

            setAssessmentApiState((prev) => ({ ...prev, ...assessmentFields }));
            await onSave(assessmentFields);
            await fetchUserReport?.();
            message.success("Assessment saved successfully");
        } catch {
            message.error("Failed to save assessment");
        } finally {
            setSavingAssessment(false);
        }
    };

    // ── Save single row ────────────────────────────────────────────────────────
    const handleSaveRow = async (idx) => {
        const f = assessmentForm.getFieldsValue();
        const pointOfMeasurement = (f[`ptMeasPoint_${idx}`] ?? "").trim();
        if (!pointOfMeasurement) {
            message.warning("Please enter a point of measurement before saving.");
            return;
        }
        setSavingRow(idx);
        try {
            const existingId = measurementIds[idx];
            if (existingId) {
                await updateMeasurement(existingId, pointOfMeasurement, idx, f);
                message.success("Measurement updated");
            } else {
                await addMeasurement(reportId, pointOfMeasurement, idx, f);
                message.success("Measurement saved");
            }
            await fetchUserReport?.();
        } catch {
            message.error("Failed to save measurement");
        } finally {
            setSavingRow(null);
        }
    };

    // ── Delete single row ──────────────────────────────────────────────────────
    const handleDeleteRow = async (idx) => {
        const measurementId = measurementIds[idx];
        setDeletingRow(idx);
        try {
            if (measurementId) await deleteMeasurementApi(measurementId);
            const cleared = { [`ptMeasPoint_${idx}`]: "" };
            [1, 2, 3, 4].forEach((col) => {
                cleared[`ptMeas_${idx}_${col}_RT`] = "";
                cleared[`ptMeas_${idx}_${col}_LT`] = "";
            });
            assessmentForm.setFieldsValue(cleared);
            await fetchUserReport?.();
            message.success("Measurement row deleted");
        } catch {
            message.error("Failed to delete measurement");
        } finally {
            setDeletingRow(null);
        }
    };

    // ── Print ──────────────────────────────────────────────────────────────────
    const handlePrintAssessment = () => {
        const f = assessmentForm.getFieldsValue();
        const html = buildPrintLymphedemaHTML({ f, lymphedemaData, patientData });
        const printWindow = window.open("", "_blank", "width=900,height=700");
        printWindow.document.write(`<!DOCTYPE html><html><head><title>Lymphedema Assessment Sheet</title>
            <style>
                * { box-sizing: border-box; margin: 0; padding: 0; }
                body { font-family: Georgia, 'Times New Roman', serif; font-size: 11px; color: #000; background: #fff; padding: 20px 28px; }
                h2 { text-align: center; font-size: 15px; font-weight: bold; text-decoration: underline; letter-spacing: .05em; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #555; }
                .field-row { display: flex; flex-wrap: wrap; gap: 6px 16px; align-items: flex-end; padding: 5px 0; border-bottom: 1px dashed #bbb; margin-bottom: 4px; }
                .field-group { display: flex; align-items: flex-end; gap: 4px; flex: 1; min-width: 100px; }
                .field-label { font-weight: bold; white-space: nowrap; font-size: 11px; }
                .field-value { border-bottom: 1px solid #555; flex: 1; min-width: 50px; padding: 1px 2px; font-size: 11px; min-height: 16px; }
                .field-value-multiline { border-bottom: 1px solid #555; flex: 1; min-width: 80px; padding: 2px 3px; min-height: 32px; font-size: 11px; white-space: pre-wrap; }
                .check-group { display: flex; gap: 8px; align-items: center; }
                .check-item { display: flex; align-items: center; gap: 3px; font-size: 11px; }
                .check-box { width: 11px; height: 11px; border: 1.5px solid #444; display: inline-flex; align-items: center; justify-content: center; font-size: 9px; flex-shrink: 0; }
                .check-box.checked::after { content: "✓"; font-weight: bold; }
                .body-section { display: flex; gap: 14px; padding: 6px 0; border-bottom: 1px dashed #bbb; margin-bottom: 5px; }
                .body-image { width: 130px; flex-shrink: 0; }
                .body-image img { width: 100%; height: auto; }
                .body-fields { flex: 1; display: flex; flex-direction: column; gap: 7px; }
                table { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 6px; }
                th, td { border: 1px solid #666; padding: 3px 4px; text-align: center; }
                th { background: #e8e8e8; font-weight: bold; }
                td.point-cell { text-align: left; }
                .signatures { display: flex; justify-content: space-between; margin-top: 30px; padding-top: 10px; border-top: 1px solid #aaa; }
                .sig-block { text-align: center; }
                .sig-line { width: 160px; border-bottom: 1px solid #555; height: 30px; margin-bottom: 4px; }
                @media print { body { padding: 8px 14px; } @page { margin: 8mm; size: A4; } }
            </style></head><body>${html}</body></html>`);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => { printWindow.print(); printWindow.close(); }, 400);
    };

    return (
        <div className="p-4  bg-white">

            {/* Action bar */}
            <div className="flex flex-wrap items-center justify-end gap-2 mb-6">
                <button
                    type="button"
                    onClick={handlePrintAssessment}
                    className="flex items-center gap-2 bg-gray-100 text-gray-700 border border-gray-300
                               px-3 sm:px-5 py-2 rounded-lg hover:bg-gray-200 transition-colors font-semibold shadow-sm text-sm"
                >
                    <FiPrinter size={16} />
                    Print
                </button>
                <button
                    type="button"
                    onClick={handleSaveAssessment}
                    disabled={savingAssessment}
                    className="flex items-center gap-2 bg-primary text-white
                               px-3 sm:px-5 py-2 rounded-lg hover:bg-primary/90 transition-colors
                               disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow text-sm"
                >
                    <FiSave size={16} />
                    {savingAssessment ? "Saving…" : "Save Assessment"}
                </button>
            </div>

            {/* Form */}
            <div className=" font-serif">
                <div className="text-center mb-5 pb-3 border-b-2 border-gray-400">
                    <h2 className="text-lg sm:text-xl font-bold underline tracking-wide text-gray-900 leading-tight">
                        Lymphedema Assessment Sheet
                    </h2>
                </div>

                <Form form={assessmentForm} layout="vertical">

                    {/* Name / Age / Gender / Date */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-2
                                    items-end pb-3 mb-3 border-b border-dashed border-gray-300">
                        <Form.Item name="ptName" label={<span className="font-semibold text-sm">Name:</span>} className="sm:col-span-2 mb-0">
                            <Input disabled className="border-0 border-b border-gray-500 rounded-none px-0 focus:shadow-none bg-transparent  disabled:bg-white disabled:text-black" />
                        </Form.Item>
                        <Form.Item name="ptAge" label={<span className="font-semibold text-sm">Age:</span>} className="mb-0">
                            <Input disabled className="border-0 border-b border-gray-500 rounded-none px-0 text-center bg-transparent disabled:bg-white disabled:text-black" />
                        </Form.Item>
                        <div className="flex flex-wrap gap-x-4 gap-y-2 items-end">
                            <Form.Item name="ptGender" label={<span className="font-semibold text-sm">Gender:</span>} className="mb-0">
                                <Checkbox.Group disabled className="flex gap-2">
                                    <Checkbox value="M">M.</Checkbox>
                                    <Checkbox value="F">F.</Checkbox>
                                </Checkbox.Group>
                            </Form.Item>
                            <Form.Item  name="ptDate" label={<span className="font-semibold text-sm">Date:</span>} className="mb-0">
                                <Input disabled placeholder="\ \" className="border-0 border-b border-gray-500 rounded-none w-24 px-0 bg-transparent disabled:bg-white disabled:text-black" />
                            </Form.Item>
                        </div>
                    </div>

                    {/* Diagnosis / Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2
                                    items-end pb-3 mb-3 border-b border-dashed border-gray-300">
                        <Form.Item name="ptDiagnosis" label={<span className="font-semibold text-sm">Diagnosis:</span>} className="mb-0">
                            <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                        </Form.Item>
                        <Form.Item name="ptPhone" label={<span className="font-semibold text-sm">Phone Num:</span>} className="mb-0">
                            <Input disabled className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent disabled:bg-white disabled:text-black" />
                        </Form.Item>
                    </div>

                    {/* Past History */}
                    <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
                        <Form.Item name="ptPastHistory" label={<span className="font-semibold text-sm">Past. History:</span>} className="mb-0">
                            <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                        </Form.Item>
                    </div>

                    {/* Chief Complain */}
                    <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
                        <Form.Item name="ptChiefComplain" label={<span className="font-semibold text-sm">Chief Complain:</span>} className="mb-0">
                            <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                        </Form.Item>
                    </div>

                    {/* Job & Occupation */}
                    <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
                        <Form.Item name="ptJob" label={<span className="font-semibold text-sm">Job & Occupation:</span>} className="mb-0">
                            <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                        </Form.Item>
                    </div>

                    {/* Pain score / distribution / lymphatic edema */}
                    <div className="flex flex-wrap gap-x-5 gap-y-3 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold whitespace-nowrap">Pain score:</span>
                            <Form.Item name="ptPainScore" className="mb-0 w-12">
                                <Input className="border-0 border-b border-gray-500 rounded-none px-0 text-center bg-transparent" />
                            </Form.Item>
                            <span className="text-sm font-semibold">/10</span>
                        </div>
                        <div className="flex items-center gap-2 flex-1 min-w-[140px]">
                            <span className="text-sm font-semibold whitespace-nowrap">Pain distribution:</span>
                            <Form.Item name="ptPainDistribution" className="mb-0 flex-1">
                                <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                            </Form.Item>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold whitespace-nowrap">Lymphatic edema:</span>
                            <Form.Item name="ptLymphaticEdema" className="mb-0">
                                <Checkbox.Group className="flex gap-2">
                                    <Checkbox value="yes">Yes</Checkbox>
                                    <Checkbox value="no">No.</Checkbox>
                                </Checkbox.Group>
                            </Form.Item>
                        </div>
                    </div>

                    {/* Radio Investigations */}
                    <div className="pb-2 mb-2 border-b border-dashed border-gray-300">
                        <Form.Item name="ptRadioInvestigations" label={<span className="font-semibold text-sm">Radio Investigations:</span>} className="mb-0">
                            <TextArea autoSize={{ minRows: 2 }} className="border-0 border-b border-gray-400 rounded-none resize-none bg-transparent focus:shadow-none" />
                        </Form.Item>
                    </div>

                    {/* Lab Notes + Results */}
                    <div className="flex flex-wrap gap-x-5 gap-y-3 items-end pb-3 mb-3 border-b border-dashed border-gray-300">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">Lab Notes:</span>
                            <Form.Item name="ptLabNotes" className="mb-0">
                                <Checkbox.Group className="flex gap-2">
                                    <Checkbox value="yes">Yes</Checkbox>
                                    <Checkbox value="no">No.</Checkbox>
                                </Checkbox.Group>
                            </Form.Item>
                        </div>
                        <div className="flex items-center gap-2 flex-1 min-w-[140px]">
                            <span className="text-sm font-semibold whitespace-nowrap">Results:</span>
                            <Form.Item name="ptLabResults" className="mb-0 flex-1">
                                <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                            </Form.Item>
                        </div>
                    </div>

                    {/* Body diagram + side fields */}
                    <div className="flex flex-col sm:flex-row gap-4 pb-3 mb-3 border-b border-dashed border-gray-300">
                        <div className="w-full sm:w-56 flex-shrink-0 flex justify-center">
                            <Image src="/lymphoImg.png" style={{ maxWidth: 224 }} />
                        </div>
                        <div className="flex-1 space-y-3">
                            <Form.Item name="ptOperations" label={<span className="font-semibold text-sm">Operations:</span>} className="mb-0">
                                <TextArea autoSize={{ minRows: 2 }} className="border-0 border-b border-gray-400 rounded-none resize-none bg-transparent focus:shadow-none" />
                            </Form.Item>
                            <Form.Item name="ptPresentHistory" label={<span className="font-semibold text-sm">Present History:</span>} className="mb-0">
                                <TextArea autoSize={{ minRows: 3 }} className="border-0 border-b border-gray-400 rounded-none resize-none bg-transparent focus:shadow-none" />
                            </Form.Item>
                            <Form.Item name="ptSkinAbnormalities" label={<span className="font-semibold text-sm">Skin Abnormalities:</span>} className="mb-0">
                                <TextArea autoSize={{ minRows: 2 }} className="border-0 border-b border-gray-400 rounded-none resize-none bg-transparent focus:shadow-none" />
                            </Form.Item>
                            <div>
                                <span className="font-semibold text-sm block mb-1">Plan of Treatment:</span>
                                <Form.Item name="ptTreatmentOptions" className="mb-1">
                                    <Checkbox.Group className="flex flex-wrap gap-x-4 gap-y-1">
                                        <Checkbox value="MLD">MLD</Checkbox>
                                        <Checkbox value="PCD">PCD</Checkbox>
                                        <Checkbox value="TECAR">TECAR</Checkbox>
                                        <Checkbox value="Bandaging">Bandaging</Checkbox>
                                        <Checkbox value="Exercises">Ex's</Checkbox>
                                    </Checkbox.Group>
                                </Form.Item>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold whitespace-nowrap">Other:</span>
                                    <Form.Item name="ptTreatmentOther" className="mb-0 flex-1">
                                        <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                                    </Form.Item>
                                </div>
                            </div>
                            <Form.Item name="ptPrecautions" label={<span className="font-semibold text-sm">Precautions:</span>} className="mb-0">
                                <Input className="border-0 border-b border-gray-500 rounded-none px-0 bg-transparent" />
                            </Form.Item>
                        </div>
                    </div>

                    {/* Sessions Num + Measurement Table */}
                    <div className="pb-3 mb-3 border-b border-dashed border-gray-300 overflow-x-auto">
                        <table className="w-full border-collapse text-sm text-center min-w-[700px]">
                            <thead>
                                <tr>
                                    <th className="border border-gray-400 bg-gray-100 font-bold px-2 py-1 text-left" colSpan={11}>
                                        <div className="flex items-center gap-3">
                                            <span className="whitespace-nowrap">Sessions Num:</span>
                                            <Form.Item name="sessionsNum" className="mb-0">
                                                <Checkbox.Group className="flex flex-wrap gap-x-4">
                                                    {["3", "6", "9", "12", "18", "24", "More"].map((n) => (
                                                        <Checkbox key={n} value={n}>{n}</Checkbox>
                                                    ))}
                                                </Checkbox.Group>
                                            </Form.Item>
                                        </div>
                                    </th>
                                </tr>
                                <tr>
                                    <th className="border border-gray-400 bg-gray-100 font-bold px-2 py-1 text-center">Date:</th>
                                    {[1, 2, 3, 4].map((col) => (
                                        <th key={col} colSpan={2} className="border border-gray-400 bg-gray-50 px-1 py-0.5">
                                            <Form.Item name={`session${col}Date`} className="mb-0">
                                                <DatePicker
                                                    format="DD/MM/YYYY"
                                                    size="small"
                                                    className="w-full border-0 border-b border-gray-500 rounded-none bg-transparent shadow-none text-xs"
                                                    placeholder="DD/MM/YYYY"
                                                />
                                            </Form.Item>
                                        </th>
                                    ))}
                                    <th className="border border-gray-400 bg-gray-100 w-16" />
                                </tr>
                                <tr>
                                    <th className="border border-gray-400 text-xs bg-gray-100 font-bold px-2 py-1 text-center">
                                        Point of Measurement
                                    </th>
                                    {[1, 2, 3, 4].map((col) => (
                                        <React.Fragment key={col}>
                                            <th className="border border-gray-400 bg-gray-100 font-semibold px-1 py-1">RT</th>
                                            <th className="border border-gray-400 bg-gray-100 font-semibold px-1 py-1">LT</th>
                                        </React.Fragment>
                                    ))}
                                    <th className="border border-gray-400 bg-gray-100 font-semibold px-1 py-1 text-center w-16">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ROW_INDICES.map((idx) => {
                                    const hasRecord       = !!measurementIds[idx];
                                    const isDeleting      = deletingRow === idx;
                                    const isSavingThisRow = savingRow === idx;
                                    return (
                                        <tr key={idx}>
                                            <td className="border border-gray-400 px-1 py-0.5">
                                                <Form.Item name={`ptMeasPoint_${idx}`} className="mb-0">
                                                    <Input
                                                        placeholder="Enter point..."
                                                        className="border-0 rounded-none px-1 bg-transparent text-xs font-medium placeholder:text-gray-300"
                                                    />
                                                </Form.Item>
                                            </td>
                                            {[1, 2, 3, 4].map((col) => (
                                                <React.Fragment key={col}>
                                                    <td className="border border-gray-400 px-1 py-0.5">
                                                        <Form.Item name={`ptMeas_${idx}_${col}_RT`} className="mb-0">
                                                            <Input className="border-0 rounded-none px-0 text-center bg-transparent text-xs" />
                                                        </Form.Item>
                                                    </td>
                                                    <td className="border border-gray-400 px-1 py-0.5">
                                                        <Form.Item name={`ptMeas_${idx}_${col}_LT`} className="mb-0">
                                                            <Input className="border-0 rounded-none px-0 text-center bg-transparent text-xs" />
                                                        </Form.Item>
                                                    </td>
                                                </React.Fragment>
                                            ))}
                                            <td className="border border-gray-400 px-1 py-0.5 text-center w-16">
                                                <div className="flex items-center justify-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSaveRow(idx)}
                                                        disabled={isSavingThisRow || isDeleting}
                                                        className="text-blue-400 hover:text-blue-600 transition-colors disabled:opacity-40"
                                                        title={hasRecord ? "Update measurement" : "Save measurement"}
                                                    >
                                                        <FiSave size={14} />
                                                    </button>
                                                    {hasRecord && (
                                                        <Popconfirm
                                                            title="Delete this measurement?"
                                                            description="This will permanently remove it from the server."
                                                            okText="Delete"
                                                            okButtonProps={{ danger: true }}
                                                            cancelText="Cancel"
                                                            onConfirm={() => handleDeleteRow(idx)}
                                                        >
                                                            <button
                                                                type="button"
                                                                disabled={isDeleting || isSavingThisRow}
                                                                className="text-red-400 hover:text-red-600 transition-colors disabled:opacity-40"
                                                                title="Delete measurement"
                                                            >
                                                                <FiTrash2 size={14} />
                                                            </button>
                                                        </Popconfirm>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Form>

                {/* PT Signatures */}
                <div className="flex  justify-between gap-6 sm:gap-0 pt-8 mt-4 border-t border-gray-300">
                    <div className="text-center">
                        <div className="w-40 border-b border-gray-500 h-8 mb-1 mx-auto" />
                        <span className="text-sm font-semibold">P.T Specialist Signature</span>
                    </div>
                    <div className="text-center">
                        <div className="w-40 border-b border-gray-500 h-8 mb-1 mx-auto" />
                        <span className="text-sm font-semibold">Consultant Signature</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Assessment2Tab({
    patientData,
    assessmentApiState,
    setAssessmentApiState,
    assessment,
    setAssessment,
    savingAssessment,
    setSavingAssessment,
    assessmentForm,
    scheduleForm,
    onSave,
    physioTherapyData,
    lymphedemaData,
    reportId,
    fetchUserReport,
    reportCount,
    currentReportIndex,
    onReportIndexChange,
}) {
    const [measurementIds, setMeasurementIds] = useState({});
    const [expandedReport, setExpandedReport] = useState(currentReportIndex ?? 0);
// console.log(patientData,"patientdata");

    useEffect(() => {
        setExpandedReport(currentReportIndex ?? 0);
    }, [currentReportIndex]);

    // Pre-fill form
    useEffect(() => {
        assessmentForm.setFieldsValue({
            ptName:   patientData?.patient.firstName + " " + patientData?.patient.lastName || "",
            ptAge:    calculateAge(patientData?.patient.birthDay) || "",
            ptGender: patientData?.patient.gender,
            ptDate:   new Date(patientData.bookingDate).toLocaleDateString(),
            ptPhone:  patientData?.patient?.phoneNumber,

            ptDiagnosis:           lymphedemaData?.diagnosis,
            ptPastHistory:         lymphedemaData?.pastHistory,
            ptChiefComplain:       lymphedemaData?.chiefComplain,
            ptJob:                 lymphedemaData?.jobAndOccupation,
            ptPainScore:           lymphedemaData?.painScore || "",
            ptPainDistribution:    lymphedemaData?.painDistribution,
            ptLymphaticEdema:      lymphedemaData?.lymphaticEdema ? ["yes"] : ["no"],
            ptRadioInvestigations: lymphedemaData?.radioInvestigations,
            ptLabNotes:            lymphedemaData?.labNotes ? ["yes"] : ["no"],
            ptLabResults:          lymphedemaData?.labResults,
            ptAssessment:          lymphedemaData?.ptAssessment,
            ptPlanOfTreatment:     lymphedemaData?.ptPlanOfTreatment,
            sessionsNum:           lymphedemaData?.sessionsNum ? [String(lymphedemaData?.sessionsNum)] : [],
            ptProgressNotes:       lymphedemaData?.progressNotes,
            ptTttGoals:            lymphedemaData?.tttGoals,
            ptDischarge:           lymphedemaData?.discharge,
            ptOtherNotes:          lymphedemaData?.otherNotes,
            ptOperations:          lymphedemaData?.operations        || "",
            ptPresentHistory:      lymphedemaData?.presentHistory    || "",
            ptSkinAbnormalities:   lymphedemaData?.skinAbnormalities || "",
            ptPrecautions:         lymphedemaData?.precautions       || "",
            ptTreatmentOptions: [
                lymphedemaData?.treatmentMLD       && "MLD",
                lymphedemaData?.treatmentPCD       && "PCD",
                lymphedemaData?.treatmentTECAR     && "TECAR",
                lymphedemaData?.treatmentBandaging && "Bandaging",
                lymphedemaData?.treatmentExercises && "Exercises",
            ].filter(Boolean),
            ptTreatmentOther: lymphedemaData?.treatmentOther || "",
        });

        const existingMeasurements = lymphedemaData?.measurements || [];
        const ids = {};
        existingMeasurements.forEach((m, idx) => { if (m.id) ids[idx] = m.id; });
        setMeasurementIds(ids);

        const measFields = {};
        [1, 2, 3, 4].forEach((col) => {
            const firstWithDate = existingMeasurements.find((m) => m[`session${col}Date`]);
            if (firstWithDate) measFields[`session${col}Date`] = dayjs(firstWithDate[`session${col}Date`]);
        });
        existingMeasurements.forEach((m, idx) => {
            measFields[`ptMeasPoint_${idx}`] = m.pointOfMeasurement ?? "";
            [1, 2, 3, 4].forEach((col) => {
                measFields[`ptMeas_${idx}_${col}_RT`] = m[`session${col}RT`] ?? "";
                measFields[`ptMeas_${idx}_${col}_LT`] = m[`session${col}LT`] ?? "";
            });
        });
        assessmentForm.setFieldsValue(measFields);
    }, [physioTherapyData, lymphedemaData]);

    const handleToggle = (index) => {
        if (expandedReport === index) {
            setExpandedReport(null);
        } else {
            setExpandedReport(index);
            onReportIndexChange?.(index);
        }
    };

    const sharedFormProps = {
        assessmentForm,
        patientData,
        lymphedemaData,
        physioTherapyData,
        savingAssessment,
        setSavingAssessment,
        setAssessmentApiState,
        onSave,
        fetchUserReport,
        reportId,
        measurementIds,
        setMeasurementIds,
    };

    // Single report — no accordion
    if (!reportCount || reportCount <= 1) {
        return (
            <div className="w-full  mx-auto px-2 sm:px-4">
                <LymphedemaFormContent {...sharedFormProps} />
            </div>
        );
    }

    return (
        <div className="w-full  mx-auto px-2 sm:px-4">

            <div className="flex items-center gap-2 mb-3 px-1">
                <FiFileText size={15} className="text-primary" />
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                    Reports ({reportCount})
                </span>
            </div>

            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm divide-y divide-gray-100">
                {Array.from({ length: reportCount }, (_, i) => {
                    const isOpen    = expandedReport === i;
                    const isCurrent = i === currentReportIndex;

                    return (
                        <div key={i}>
                            {/* Tab header */}
                            <button
                                type="button"
                                onClick={() => handleToggle(i)}
                                className={`w-full flex items-center justify-between px-4 py-3 text-sm font-semibold transition-colors
                                    ${isOpen
                                        ? "bg-primary/5 text-primary"
                                        : "bg-white hover:bg-gray-50 text-gray-700"
                                    }`}
                            >
                                <span className="flex items-center gap-2">
                                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold
                                        ${isCurrent ? "bg-primary text-white" : "bg-gray-200 text-gray-600"}`}>
                                        {i + 1}
                                    </span>
                                    Report {i + 1}
                                    {isCurrent && (
                                        <span className="text-[10px] font-semibold bg-primary/15 text-primary px-2 py-0.5 rounded-full">
                                            Current
                                        </span>
                                    )}
                                </span>
                                {isOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                            </button>

                            {/* Inline expanded content */}
                            {isOpen && (
                                <div className="border-t border-primary/20">
                                    <LymphedemaFormContent {...sharedFormProps} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}